import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { AdminPanelSettings } from "@mui/icons-material";
import { useNotify, useRecordContext } from "react-admin";
import {
  ensureSupplierSuperAdminRole,
  TEnsureSuperAdminResponse,
} from "@/api/resources/suppliers";
import { TSupplier } from "@/api/types/supplier";

// Botón del header de la vista de proveedor: crea (o devuelve, si ya existe) el
// rol "Super Admin" del supplier con todos los permisos. Idempotente. Sirve para
// dejar al tenant con un rol admin que le permita autogestionar permisos desde
// el portal (el frontend habilita la pestaña de Roles por ese nombre exacto).
export const SupplierEnsureAdminRoleButton = () => {
  const record = useRecordContext<TSupplier>();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TEnsureSuperAdminResponse | null>(null);

  if (!record) return null;

  const close = () => {
    if (loading) return;
    setOpen(false);
    setResult(null);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await ensureSupplierSuperAdminRole(String(record.id));
      setResult(res);
      notify(
        res.created
          ? `Rol "Super Admin" creado (${res.role.permissions?.length ?? 0} permisos)`
          : `El rol "Super Admin" ya existía (${res.role.permissions?.length ?? 0} permisos)`,
        { type: res.created ? "success" : "info" }
      );
    } catch (e: any) {
      notify(
        e?.response?.data?.message ?? "Error al crear el rol Super Admin",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        color="primary"
        startIcon={<AdminPanelSettings />}
        onClick={() => setOpen(true)}
      >
        Rol Super Admin
      </Button>
      <Dialog open={open} onClose={close}>
        <DialogTitle>Rol "Super Admin" del proveedor</DialogTitle>
        <DialogContent>
          {result ? (
            <Alert severity={result.created ? "success" : "info"} sx={{ mb: 1 }}>
              {result.created
                ? "Rol creado correctamente."
                : "El rol ya existía; no se modificó."}
              <br />
              <strong>{result.role.name}</strong> · {result.role.permissions?.length ?? 0} permisos
              <br />
              ID: {result.role.id}
            </Alert>
          ) : (
            <DialogContentText>
              Se creará el rol <strong>Super Admin</strong> (con todos los
              permisos) para <strong>{record.name}</strong>. Si ya existe, no se
              recrea ni se modifican sus permisos, solo se muestra.
              <br />
              <br />
              Este rol habilita el acceso total y la autogestión de permisos
              desde el portal del proveedor.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={loading}>
            {result ? "Cerrar" : "Cancelar"}
          </Button>
          {!result && (
            <Button
              color="primary"
              variant="contained"
              disabled={loading}
              onClick={handleConfirm}
            >
              {loading ? "Procesando..." : "Crear / asegurar rol"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
